import { Box, Stack } from "@mui/material";
import React from "react";
import { SSButton } from "src/components/base/components/SSButton";
import { SSText } from "src/components/base/components/SSText";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { useSSAuth } from "src/components/reusable/hooks/useSSAuth";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { useTicketmaster } from "src/components/reusable/hooks/useTicketmaster";
import { TRegistrationCardData } from "src/shared/types/globalStotre.types";
import { constants } from "src/shared/constants";
import { routes } from "src/shared/routes";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";
import { useSeatGeek } from "src/components/reusable/hooks/useSeatGeek";
import { useTicketsdotcom } from "src/components/reusable/hooks/useTicketsdotcom";

export const RegistrationCard = (props: {
  cardData: TRegistrationCardData;
  onClickCTA?: (cardItem: TRegistrationCardData) => Promise<void>;
}) => {
  const router = useRouterExt();
  const theme = useSSTheme();
  const { user } = useSSAuth();
  const { connectTicketmaster } = useTicketmaster();
  const [isLoading, setIsLoading] = React.useState(false);
  const { VENDOR } = useTeamConfig();
  const { connectSeatGeek } = useSeatGeek();
  const { connectTicketsdotcom } = useTicketsdotcom();

  const onClickCTA = async () => {
    setIsLoading(true);

    if (props?.onClickCTA) {
      await props?.onClickCTA(props?.cardData);
      setIsLoading(false);
      return;
    }

    if (!user) {
      const state = {
        authOrigin: constants.authOrigins.landingRegistrationCard,
        redirectRoute: {
          pathname: props?.cardData?.authRedirectPage || routes.home,
        },
      };

      if (VENDOR === constants.vendorUUIDs.seatgeek) {
        return await connectSeatGeek(state);
      }

      if (VENDOR === constants.vendorUUIDs.ticketsdotcom) {
        return await connectTicketsdotcom(state);
      }

      return await connectTicketmaster({ state });
    }
    await router.pushSchema(props.cardData.authRedirectPage);
    setIsLoading(false);
  };

  return (
    <Stack
      direction={"column"}
      border={`1px solid ${theme.colors.border}`}
      borderRadius={2.5}
      alignItems={"flex-start"}
      overflow={"hidden"}
      height={"100%"}
    >
      <Stack p={2} spacing={2} flex={1} width={"100%"}>
        <Stack spacing={2}>
          <SSText
            variant="h2"
            color={theme.colors.teamPrimary}
            lineHeight={1.1}
            fontWeight={500}
            textAlign={"center"}
            sx={{ ...props?.cardData?.titleStyles }}
          >
            {props?.cardData?.title}
          </SSText>
          <SSText
            variant="body"
            color={theme.colors.gray[400]}
            textAlign={"center"}
          >
            {props?.cardData?.description}
          </SSText>
        </Stack>

        <Box flexGrow={1} />
        <Stack spacing={2} width={"100%"}>
          <SSButton
            size="large"
            fullWidth
            variant={props?.cardData?.buttonVariant || "outlined"}
            onClick={onClickCTA}
            loading={isLoading}
            disabled={isLoading}
            sx={{ fontWeight: 500, ...props?.cardData?.primaryButtonStyles }}
          >
            {props?.cardData?.buttonLabel || "View Offers"}
          </SSButton>

          {props?.cardData?.secondaryButton || null}
        </Stack>
      </Stack>
    </Stack>
  );
};
